import axios, {
  type AxiosInstance,
  type AxiosResponse,
  type InternalAxiosRequestConfig,
} from "axios";
import { getToken, redirectToLogin } from "@/utils/auth";
import { alertError } from "@/utils/message";

/** 业务响应结构（按后端约定可调整） */
export interface ApiResponse<T = unknown> {
  code?: number | string;
  error?: number | string | boolean;
  message?: string;
  msg?: string;
  data?: T;
}

const UNAUTH_CODE = 498;
const DEFAULT_UNAUTH_MSG = "系统错误，API token 参数丢失";

let handlingUnauth = false;

function isUnauthCode(value: unknown): boolean {
  return Number(value) === UNAUTH_CODE;
}

function extractErrorMessage(data: unknown, fallback = DEFAULT_UNAUTH_MSG): string {
  if (!data || typeof data !== "object") return fallback;
  const body = data as ApiResponse & { error_msg?: string };
  const msg = body.message || body.msg || body.error_msg;
  if (typeof msg === "string" && msg.trim()) return msg;
  return fallback;
}

/** 弹窗提示错误信息后，再跳转登录 */
async function handleUnauth(data?: unknown) {
  if (handlingUnauth) return;
  handlingUnauth = true;
  try {
    const message = extractErrorMessage(data);
    await alertError(message, "登录失效");
    redirectToLogin();
  } finally {
    handlingUnauth = false;
  }
}

const request: AxiosInstance = axios.create({
  timeout: 30000,
  headers: {
    "Content-Type": "application/json",
  },
});

// 请求拦截器：token 仅通过 Authorization Bearer 传递，不拼接到 URL / body
request.interceptors.request.use(
  (config: InternalAxiosRequestConfig) => {
    const token = getToken();
    if (token) {
      config.headers.set("Authorization", `Bearer ${token}`);
    }

    // 防止业务侧误把 token 放进 query
    if (config.params && typeof config.params === "object" && "token" in config.params) {
      const { token: _ignored, ...rest } = config.params as Record<string, unknown>;
      config.params = rest;
    }

    return config;
  },
  (error) => Promise.reject(error),
);

// 响应拦截器
request.interceptors.response.use(
  (response: AxiosResponse) => {
    const { data, status } = response;

    // HTTP 状态码 498
    if (status === UNAUTH_CODE) {
      void handleUnauth(data);
      return Promise.reject(response);
    }

    // 业务码 498（常见字段：code / error）
    if (data && typeof data === "object" && !(data instanceof Blob)) {
      const body = data as ApiResponse;
      if (isUnauthCode(body.code) || isUnauthCode(body.error)) {
        void handleUnauth(data);
        return Promise.reject(data);
      }
    }

    return response;
  },
  (error) => {
    if (error.response) {
      const { status, data } = error.response;
      if (
        status === UNAUTH_CODE ||
        isUnauthCode(data?.code) ||
        isUnauthCode(data?.error)
      ) {
        void handleUnauth(data);
      }
    }
    return Promise.reject(error);
  },
);

export default request;
